let idCounter = 0;
export const generateId = (tag) => `${tag}-${Date.now()}-${idCounter++}`;

// id khusus buat "row Canvas" di ElementsPanel — bukan id elemen SVG asli,
// dipakai buat nandain kalau yang lagi dipilih itu si canvas, bukan sebuah shape
export const CANVAS_ID = "__canvas__";

// Tag yang dianggap "shape" — ini yang ditampilkan & bisa diseleksi/animate di ElementsPanel.
// Tag lain (defs, style, gradient, clipPath, dll) TETAP diparse & dirender apa adanya
// (supaya visual SVG-nya gak rusak/beda dari aslinya), cuma disembunyikan dari panel
// karena gak masuk akal buat diseleksi/di-drag.
const SHAPE_TAGS = [
  "path", "circle", "rect", "ellipse", "line",
  "polygon", "polyline", "g", "text", "use", "image",
];
export function isShapeTag(tag) {
  return SHAPE_TAGS.includes(tag);
}

// tag yang isi pentingnya adalah TEXT CONTENT-nya (bukan children element), misalnya
// <style>.cls-1{fill:red}</style> atau <text>Hello</text>. Kalau ini gak ditangani
// khusus, isi teksnya keburu ke-drop pas parsing (karena text node bukan nodeType 1),
// dan hasilnya beda sama SVG aslinya (warna hilang / teks kosong).
const TEXT_CONTENT_TAGS = ["style", "title", "desc", "text", "tspan"];

function nodeToTree(node) {
  if (node.nodeType !== 1) return null; // skip text/comment nodes di level ini
  // PENTING: JANGAN .toLowerCase() nama tag. SVG itu XML, case-sensitive — tag kayak
  // <linearGradient>, <radialGradient>, <feColorMatrix>, <feGaussianBlur>, <clipPath>,
  // dll WAJIB camelCase. DOMParser (mode "image/svg+xml") sudah preserve case aslinya
  // dengan benar; kalau di-lowercase manual di sini, tag itu jadi nama yang gak valid/gak
  // dikenali browser (mis. "lineargradient"), dan hasilnya elemen itu gagal dirender —
  // ini penyebab gradient/filter/drop-shadow hilang atau warnanya berubah.
  const tag = node.tagName;

  const attrs = {};
  for (const attr of node.attributes) {
    attrs[attr.name] = attr.value;
  }

  // pastikan tiap elemen punya id unik buat jadi target GSAP/CSS nanti
  const id = attrs.id || generateId(tag);
  attrs.id = id;

  const elementChildren = Array.from(node.childNodes).filter((n) => n.nodeType === 1);
  const children = elementChildren.map(nodeToTree).filter(Boolean);

  // kalau tag ini butuh text content (style/title/desc/text sederhana tanpa nested tag),
  // simpan teksnya supaya gak hilang pas dirender ulang
  const textContent =
    TEXT_CONTENT_TAGS.includes(tag) && elementChildren.length === 0
      ? node.textContent
      : undefined;

  return {
    id,
    tag,
    attrs,
    children,
    textContent,
    hidden: false,
    locked: false,
    // transform state terpisah dari attrs asli SVG, ini yang dianimasikan
    transform: {
      x: 0,
      y: 0,
      rotate: 0,
      scaleX: 1,
      scaleY: 1,
      skewX: 0,
      skewY: 0,
      originX: 0,
      originY: 0,
    },
  };
}

export function parseSvgString(svgString) {
  const parser = new DOMParser();
  const doc = parser.parseFromString(svgString, "image/svg+xml");

  const errorNode = doc.querySelector("parsererror");
  if (errorNode) {
    throw new Error("File SVG tidak valid atau corrupt.");
  }

  const svgEl = doc.documentElement;
  if (svgEl.tagName.toLowerCase() !== "svg") {
    throw new Error("File yang diupload bukan SVG.");
  }

  const viewBoxAttr = svgEl.getAttribute("viewBox");
  const widthAttr = svgEl.getAttribute("width");
  const heightAttr = svgEl.getAttribute("height");

  let viewBox = viewBoxAttr;
  let width = widthAttr ? parseFloat(widthAttr) : null;
  let height = heightAttr ? parseFloat(heightAttr) : null;

  if (!viewBox) {
    const w = width || 100;
    const h = height || 100;
    viewBox = `0 0 ${w} ${h}`;
  }
  if (!width || !height) {
    const parts = viewBox.split(/\s+/).map(Number);
    width = width || parts[2] || 100;
    height = height || parts[3] || 100;
  }

  const tree = Array.from(svgEl.childNodes)
    .map(nodeToTree)
    .filter(Boolean);

  return { viewBox, width, height, tree };
}

// bungkus hasil parse 1 file SVG jadi 1 "grup" top-level di elementTree, supaya
// beberapa file SVG bisa diimport sekaligus dan tetap kepisah rapi di ElementsPanel
// (mirip konsep layer/folder), bukan tercampur jadi satu tree.
export function createGroupNode(name, children) {
  const id = generateId("group");
  return {
    id,
    tag: "g",
    attrs: { id },
    children,
    hidden: false,
    locked: false,
    name: name || id,
    transform: {
      x: 0, y: 0, rotate: 0, scaleX: 1, scaleY: 1, skewX: 0, skewY: 0, originX: 0, originY: 0,
    },
  };
}

// baca 1 File object (dari <input type="file"> atau drag&drop) jadi hasil parse siap pakai.
// Dipakai bareng oleh ImportPanel (drag&drop pertama) dan tombol "+ SVG" (import tambahan).
export function readSvgFile(file) {
  return new Promise((resolve, reject) => {
    if (!file || !file.name.toLowerCase().endsWith(".svg")) {
      reject(new Error("Saat ini cuma support file .svg."));
      return;
    }
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const parsed = parseSvgString(e.target.result);
        if (!parsed.tree.length) {
          reject(new Error("SVG berhasil dibaca tapi tidak ada elemen yang bisa dianimasikan."));
          return;
        }
        resolve({ ...parsed, fileName: file.name.replace(/\.svg$/i, "") });
      } catch (err) {
        reject(err);
      }
    };
    reader.onerror = () => reject(new Error("Gagal membaca file."));
    reader.readAsText(file);
  });
}

// flatten tree jadi list datar, dipakai ElementsPanel & lookup cepat
export function flattenTree(tree, depth = 0, result = []) {
  tree.forEach((node) => {
    result.push({
      id: node.id,
      tag: node.tag,
      depth,
      hasChildren: !!node.children?.length,
      hidden: !!node.hidden,
      locked: !!node.locked,
      name: node.name,
    });
    if (node.children?.length) flattenTree(node.children, depth + 1, result);
  });
  return result;
}

// cari 1 node by id di dalam tree (recursive)
export function findNodeById(tree, id) {
  for (const node of tree) {
    if (node.id === id) return node;
    if (node.children?.length) {
      const found = findNodeById(node.children, id);
      if (found) return found;
    }
  }
  return null;
}

// update 1 node secara immutable, dipakai reducer UPDATE_TRANSFORM
export function updateNodeInTree(tree, id, updater) {
  return tree.map((node) => {
    if (node.id === id) return updater(node);
    if (node.children?.length) {
      return { ...node, children: updateNodeInTree(node.children, id, updater) };
    }
    return node;
  });
}
